import { BTN_GHOST } from '~/components/workbench/tools/payments/ui';
import { MAX_OPTION_SETS, parseOptionDrafts, previewSku, type OptionDraft } from './shop-model';

/**
 * Up to three options (name + comma-separated values), with a live variant
 * count, the first problem the server would reject, and a SKU preview.
 */
export function OptionsFields({
  drafts,
  onChange,
  productTitle,
  skuPattern,
  disabled,
}: {
  drafts: OptionDraft[];
  onChange: (next: OptionDraft[]) => void;
  productTitle: string;
  skuPattern: string;
  disabled?: boolean;
}) {
  const parsed = parseOptionDrafts(drafts);
  const update = (i: number, patch: Partial<OptionDraft>) =>
    onChange(drafts.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const first = parsed.ok ? parsed.options.map((o) => o.values[0]) : [];

  return (
    <div className="space-y-2">
      <span className="block text-[11px] text-secondary">Options</span>

      {drafts.map((draft, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            value={draft.name}
            onChange={(e) => update(i, { name: e.target.value })}
            placeholder={['Size', 'Colour', 'Material'][i] ?? 'Option'}
            aria-label={`Option ${i + 1} name`}
            disabled={disabled}
            className="ui-input !w-28 shrink-0 !py-2"
          />
          <input
            value={draft.values}
            onChange={(e) => update(i, { values: e.target.value })}
            placeholder={['S, M, L', 'Blue, Red', 'Cotton, Linen'][i] ?? 'Values, comma separated'}
            aria-label={`Option ${i + 1} values`}
            disabled={disabled}
            className="ui-input min-w-0 flex-1 !py-2"
          />
          <button
            type="button"
            onClick={() => onChange(drafts.filter((_, j) => j !== i))}
            disabled={disabled}
            className="ui-btn-icon shrink-0"
            aria-label={`Remove option ${i + 1}`}
            title="Remove option"
          >
            <div className="i-ph:x text-[13px]" />
          </button>
        </div>
      ))}

      <div className="flex flex-wrap items-center justify-between gap-2">
        {drafts.length < MAX_OPTION_SETS ? (
          <button
            type="button"
            onClick={() => onChange([...drafts, { name: '', values: '' }])}
            disabled={disabled}
            className={BTN_GHOST}
          >
            <div className="i-ph:plus text-[13px]" />
            Add option
          </button>
        ) : (
          <span />
        )}
        <span className="text-[11px] text-muted">
          {parsed.ok ? `${parsed.variantCount} variant${parsed.variantCount === 1 ? '' : 's'}` : ''}
        </span>
      </div>

      {!parsed.ok && <p className="text-[11px] leading-relaxed text-warning">{parsed.error}</p>}

      {parsed.ok && productTitle.trim() && (
        <p className="text-[11px] leading-relaxed text-muted">
          SKUs follow your pattern <code className="font-geist-mono text-secondary">{skuPattern}</code>, e.g.{' '}
          <code className="font-geist-mono text-secondary">
            {previewSku(skuPattern, { product: productTitle, options: first })}
          </code>
          . You can edit each SKU afterwards.
        </p>
      )}
    </div>
  );
}
